import {inject, Injectable} from '@angular/core';
import {map, Observable, timeout} from 'rxjs';
import {CommunicationService} from '../../http/communication.service';
import {ISO_DATE_ONLY_REGEX, toPolarisOrigin} from './polaris.utils';
import {
  PolarisAccountKey,
  PolarisApiChatMessagesResponseDto,
  PolarisApiChatsOverviewResponseDto,
  PolarisApiChatUploadDto,
  PolarisApiWidgetContextDto,
  PolarisApiMessageUploadDto,
  PolarisApiUploadChatsRequestDto,
  PolarisApiUploadChatsResponseDto,
  PolarisChatAggregate,
  PolarisChatOverview,
  PolarisModelVersion,
  PolarisSendMessageInput,
  PolarisWidgetContext,
  PolarisTurn,
  PolarisPaginationParams,
  PolarisChatsOverviewResult
} from './model';

@Injectable({
  providedIn: 'root'
})
export class PolarisApiService {
  private communicationService = inject(CommunicationService);

  public getChatsOverview(
    accountKey: PolarisAccountKey,
    pagination: PolarisPaginationParams = {}
  ): Observable<PolarisChatsOverviewResult> {
    const path = this.getChatsOverviewPath(accountKey);
    const params = {
      limit: pagination.limit ?? 50,
      offset: pagination.offset ?? 0
    };
    return this.communicationService
      .httpGet<PolarisApiChatsOverviewResponseDto>(path, {params})
      .pipe(map(dto => this.mapChatsOverviewResponse(dto)));
  }

  public uploadChats(
    accountKey: PolarisAccountKey,
    chats: PolarisChatAggregate[]
  ): Observable<PolarisChatAggregate | null> {
    const body: PolarisApiUploadChatsRequestDto = {
      chats: chats.map(chat => this.toApiChatUpload(chat))
    };
    const path = this.getUploadChatsPath(accountKey);

    return this.communicationService
      .httpPost<PolarisApiUploadChatsResponseDto | null>(path, body)
      .pipe(map(() => null));
  }

  private mapChatsOverviewResponse(dto: PolarisApiChatsOverviewResponseDto): PolarisChatsOverviewResult {
    return {
      items: dto.items.map(item => this.toChatOverview(item)),
      total: dto.total,
      limit: dto.limit,
      offset: dto.offset
    };
  }

  private toChatOverview(dto: PolarisApiChatMessagesResponseDto['chat']): PolarisChatOverview {
    return {
      chatId: dto.chatId,
      accountKey: dto.accountKey,
      userId: dto.userId,
      title: dto.title ?? 'New Chat',
      parentChatId: dto.parentChatId,
      modelVersion: this.toDomainModelVersion(dto.modelVersion),
      createdAt: dto.createdAt,
      lastModified: dto.lastModified,
      lastMessageTime: dto.lastMessageTime,
      isShared: dto.isShared
    };
  }

  private toStrictDateRange(
    dateRange: PolarisApiWidgetContextDto['dateRange'] | NonNullable<PolarisSendMessageInput['message']['widgetContext']>['dateRange']
  ): {from: string; to: string} | undefined {
    if (!dateRange) {
      return undefined;
    }
    if (typeof dateRange === 'string') {
      throw new Error(`Invalid dateRange type. Expected { from: string; to: string }, got "${dateRange}".`);
    }

    this.assertIsoDateOnly(dateRange.from, 'from');
    this.assertIsoDateOnly(dateRange.to, 'to');

    return {
      from: dateRange.from,
      to: dateRange.to
    };
  }

  private assertIsoDateOnly(value: string, fieldName: 'from' | 'to'): void {
    if (!ISO_DATE_ONLY_REGEX.test(value)) {
      throw new Error(`Invalid dateRange.${fieldName} format. Expected YYYY-MM-DD, got "${value}".`);
    }

    const [yearPart, monthPart, dayPart] = value.split('-');
    const year = Number(yearPart);
    const month = Number(monthPart);
    const day = Number(dayPart);
    const parsedDate = new Date(Date.UTC(year, month - 1, day));

    if (
      Number.isNaN(parsedDate.getTime()) ||
      parsedDate.getUTCFullYear() !== year ||
      parsedDate.getUTCMonth() + 1 !== month ||
      parsedDate.getUTCDate() !== day
    ) {
      throw new Error(`Invalid dateRange.${fieldName} value. Expected a real calendar date in YYYY-MM-DD format, got "${value}".`);
    }
  }

  private toApiChatUpload(chat: PolarisChatAggregate): PolarisApiChatUploadDto {
    const messages: PolarisApiMessageUploadDto[] = chat.turns.map(turn => ({
      id: turn.messagePair.id,
      brandKey: turn.myBrand,
      competitorsKeys: turn.myCompetitors,
      userPrompt: turn.messagePair.userPrompt,
      response: turn.messagePair.response,
      userTimestamp: this.toUploadTimestamp(turn.messagePair.userTimestamp),
      author: this.toUploadAuthor(turn),
      widgetContext: this.toApiUploadWidgetContext(turn),
      insights: turn.messagePair.insights ?? undefined,
      graphs: turn.messagePair.graphs.length ? turn.messagePair.graphs.map(graph => ({
        title: graph.title,
        type: graph.type,
        url: graph.url,
        x: graph.x,
        series: graph.series.map(series => ({
          name: series.name,
          y: series.y,
          unit: series.unit
        })),
        y_is_percentage: graph.yIsPercentage,
        x_axis: graph.xAxis,
        y_axis: graph.yAxis
      })) : undefined,
      creativesURLs: turn.messagePair.creativesURLs,
      followUpQuestions: turn.messagePair.followUpQuestions
    }));

    return {
      chatId: chat.overview.chatId,
      title: chat.overview.title,
      lastUpdated: this.toUploadTimestamp(chat.overview.lastModified ?? chat.overview.lastMessageTime ?? chat.overview.createdAt),
      messages
    };
  }

  private toUploadAuthor(turn: PolarisTurn): PolarisApiMessageUploadDto['author'] {
    const hasAssistantPayload = Boolean(
      turn.messagePair.response.trim() ||
      turn.messagePair.followUpQuestions.length ||
      turn.messagePair.creativesURLs.length ||
      turn.messagePair.graphs.length ||
      (turn.messagePair.insights?.length ?? 0) > 0
    );
    return hasAssistantPayload ? 'assistant' : 'user';
  }

  private toApiUploadWidgetContext(turn: PolarisTurn): PolarisApiWidgetContextDto | undefined {
    const widgetContext = turn.widgetContext;
    if (!widgetContext) {
      return undefined;
    }

    const origin = widgetContext.origin ?? turn.origin;
    const name = widgetContext.name ?? turn.widgetName;
    const myBrand = widgetContext.myBrand ?? turn.myBrand;
    const myCompetitors = widgetContext.myCompetitors ?? turn.myCompetitors ?? [];
    const dateRange = this.safeToStrictDateRange(widgetContext.dateRange);

    if (!origin || !name || !myBrand || !widgetContext.device || !dateRange) {
      return undefined;
    }

    return {
      origin,
      name,
      metrics: widgetContext.metrics ?? [],
      myBrand,
      myCompetitors,
      dateRange,
      countries: widgetContext.countries ?? [],
      device: widgetContext.device
    };
  }

  private safeToStrictDateRange(dateRange: PolarisWidgetContext['dateRange']): {from: string; to: string} | undefined {
    try {
      return this.toStrictDateRange(dateRange);
    } catch {
      // Legacy widget context may have malformed date ranges; omit instead of failing migration upload.
      return undefined;
    }
  }

  private toUploadTimestamp(value: number | string | undefined): number {
    if (typeof value === 'number' && value > 0 && !Number.isNaN(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
    return Date.now();
  }

  private getChatsOverviewPath(accountKey: PolarisAccountKey): string {
    return `/polaris/v2/accounts/${accountKey}/chats`;
  }
  private getUploadChatsPath(accountKey: PolarisAccountKey): string {
    return `/polaris/v2/accounts/${accountKey}/chats`;
  }

  private toDomainModelVersion(value: string | undefined): PolarisModelVersion | undefined {
    if (value === 'v1' || value === 'v2') {
      return value;
    }
    return undefined;
  }
}
